type V3 = [number, number, number];
declare const __WASM_REVISION__: string;
type View = { position: V3; target: V3; fov: number };
interface Engine {
  setCamera(position: Float32Array, target: Float32Array, fov: number): void;
  setHour(hour: number): void;
  resize(width: number, height: number, dpr: number): void;
  render(seconds: number): unknown;
  stats(): string;
  frameDone(): Promise<void>;
  groundHeight?(x: number, z: number): number;
}
interface SceneApi {
  ready: boolean; errors: string[]; engine: Engine | null;
  stats(): Record<string, unknown>;
  setHour(hour: number): void;
  setView(name: string): void;
  setCamera(position: V3, target: V3, fov?: number): void;
  camera(): View;
  frameDone(): Promise<void>;
}
declare global { interface Window { __sceneReady?: boolean; __shirakawago: SceneApi; __SCENE_ERRORS__: string[] } }

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const canvas = $<HTMLCanvasElement>('world');
const loading = $('loading');
const params = new URLSearchParams(location.search);
// Choose before generating any geometry: a desktop mesh alone exceeds a phone's
// practical Safari memory budget. The query override is for reproducible QA.
const touchDevice = matchMedia('(pointer: coarse)').matches || /iPhone|iPad|Android/i.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const mobile = params.get('quality') === 'mobile' || (params.get('quality') !== 'desktop' && touchDevice);
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const finite = (key: string, fallback: number) => {
  const value = params.get(key);
  return value !== null && value.trim() !== '' && Number.isFinite(Number(value)) ? Number(value) : fallback;
};
const parseVec = (key: string): V3 | null => {
  const value = params.get(key)?.split(',').map(Number);
  return value?.length === 3 && value.every(Number.isFinite) ? value as V3 : null;
};
const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const scale = (a: V3, n: number): V3 => [a[0] * n, a[1] * n, a[2] * n];
const cross = (a: V3, b: V3): V3 => [a[1]*b[2]-a[2]*b[1], a[2]*b[0]-a[0]*b[2], a[0]*b[1]-a[1]*b[0]];
const norm = (v: V3): V3 => scale(v, 1 / (Math.hypot(...v) || 1));

const portrait = innerWidth / innerHeight < .85;
const VIEWS: Record<string, View> = {
  village: { position: [67, 59, 97], target: [0, 8, 0], fov: portrait ? 49 : 36 },
  river: { position: [47, 27, 83], target: [2, 6, 17], fov: portrait ? 51 : 43 },
  minkaen: { position: [-49, 32, 80], target: [-86, 8, 18], fov: portrait ? 48 : 42 },
};
interface Pose { target: V3; azimuth: number; elevation: number; distance: number; fov: number }
const poseOf = (v: View): Pose => {
  const d = sub(v.position, v.target), distance = Math.hypot(...d);
  return { target: [...v.target], azimuth: Math.atan2(d[0], d[2]), elevation: Math.asin(d[1] / distance), distance, fov: v.fov };
};
const clone = (p: Pose): Pose => ({ ...p, target: [...p.target] });
const positionOf = (p: Pose): V3 => add(p.target, [Math.sin(p.azimuth) * Math.cos(p.elevation) * p.distance, Math.sin(p.elevation) * p.distance, Math.cos(p.azimuth) * Math.cos(p.elevation) * p.distance]);
let currentView = VIEWS[params.get('view') ?? 'village'] ? params.get('view') ?? 'village' : 'village';
const initial: View = { ...VIEWS[currentView], position: parseVec('cam') ?? VIEWS[currentView].position, target: parseVec('target') ?? VIEWS[currentView].target, fov: clamp(finite('fov', VIEWS[currentView].fov), 20, 65) };
let pose = poseOf(initial), goal = clone(pose);
let engine: Engine | null = null;
let hour = clamp(finite('time', 17.25), 0, 24);
let frame = 0, last = 0, liveSeconds = 0;
const maxPendingFrames = params.get('inflight') === '1' ? 1 : 2;
let gpuPending = 0, renderDpr = 1, adaptiveDpr = 1.15;
let resizePending = true;
let gpuFrameMs = 0, fps = 0, timingFrames = 0, timingStart = 0, lastAdaptation = 0;
let startupMs = 0, buildMs = 0;
const errors: string[] = [];
const pointers = new Map<number, { x: number; y: number; button: number; shift: boolean }>();
let pinch: { distance: number; x: number; y: number } | null = null;
function safePosition(viewPose: Pose = pose): V3 {
  const p = positionOf(viewPose);
  if (engine?.groundHeight) p[1] = Math.max(p[1], engine.groundHeight(p[0], p[2]) + 2.2);
  return p;
}
const overlays = [...document.querySelectorAll<HTMLElement>('.overlay')];
for (const overlay of overlays) overlay.inert = true;
canvas.inert = true;

function setHour(value: number) {
  if (!Number.isFinite(value)) return;
  hour = clamp(value, 0, 24);
  engine?.setHour(hour);
  $<HTMLInputElement>('time-slider').value = String(clamp(hour, 12, 22));
  const minute = Math.round(hour * 60) % 1440;
  const time = `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
  const name = hour < 5 || hour >= 20 ? 'Winter night' : hour < 8 ? 'First light' : hour < 15.5 ? 'Winter daylight' : hour < 17 ? 'Golden afternoon' : hour < 18.5 ? 'Blue hour' : 'Evening lights';
  $('time-slider').setAttribute('aria-valuetext', `${time}, ${name}`);
}
function setView(name: string) {
  if (!VIEWS[name]) return;
  currentView = name;
  setCamera(VIEWS[name].position, VIEWS[name].target, VIEWS[name].fov, false);
}
function setCamera(position: V3, target: V3, fov = 42, immediate = true) {
  if (![...position, ...target, fov].every(Number.isFinite) || Math.hypot(...sub(position, target)) < 1) return;
  goal = poseOf({ position, target, fov: clamp(fov, 20, 65) });
  goal.azimuth = pose.azimuth + Math.atan2(Math.sin(goal.azimuth - pose.azimuth), Math.cos(goal.azimuth - pose.azimuth));
  if (immediate || reducedMotion) pose = clone(goal);
}
function moved() {
  currentView = 'custom';
}
function pan(dx: number, dy: number) {
  const amount = goal.distance * Math.tan(goal.fov * Math.PI / 360) * 2 / canvas.clientHeight;
  const right: V3 = [Math.cos(goal.azimuth), 0, -Math.sin(goal.azimuth)];
  const ahead: V3 = [-Math.sin(goal.azimuth), 0, -Math.cos(goal.azimuth)];
  goal.target = add(goal.target, add(scale(right, -dx * amount), scale(ahead, -dy * amount)));
  goal.target[0] = clamp(goal.target[0], -240, 240);
  goal.target[2] = clamp(goal.target[2], -240, 240);
  if (engine?.groundHeight) goal.target[1] = engine.groundHeight(goal.target[0], goal.target[2]) + 6;
}
function groundAt(x: number, y: number): V3 | null {
  const position = safePosition(goal), forward = norm(sub(goal.target, position));
  const right = norm(cross(forward, [0, 1, 0])), up = cross(right, forward);
  const rect = canvas.getBoundingClientRect(), t = Math.tan(goal.fov * Math.PI / 360);
  const dir = norm(add(forward, add(scale(right, ((x - rect.left) / rect.width * 2 - 1) * t * rect.width / rect.height), scale(up, (1 - (y - rect.top) / rect.height * 2) * t))));
  const distance = (goal.target[1] - position[1]) / dir[1];
  return dir[1] < -.015 && distance > 0 && distance < 2000 ? add(position, scale(dir, distance)) : null;
}
function zoom(factor: number, x: number, y: number) {
  const before = groundAt(x, y);
  goal.distance = clamp(goal.distance * factor, 17, 760);
  const after = groundAt(x, y);
  if (before && after) {
    goal.target = add(goal.target, sub(before, after));
    goal.target[0] = clamp(goal.target[0], -240, 240);
    goal.target[2] = clamp(goal.target[2], -240, 240);
  }
}
canvas.addEventListener('contextmenu', e => e.preventDefault());
canvas.addEventListener('pointerdown', e => {
  canvas.focus({ preventScroll: true });
  canvas.setPointerCapture(e.pointerId);
  pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, button: e.button, shift: e.shiftKey });
  pinch = null; moved();
});
canvas.addEventListener('pointermove', e => {
  const previous = pointers.get(e.pointerId);
  if (!previous) return;
  const dx = e.clientX - previous.x, dy = e.clientY - previous.y;
  pointers.set(e.pointerId, { ...previous, x: e.clientX, y: e.clientY });
  if (pointers.size === 2) {
    const [a, b] = [...pointers.values()];
    const next = { distance: Math.hypot(a.x - b.x, a.y - b.y), x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    if (pinch && next.distance > 5) { zoom(pinch.distance / next.distance, next.x, next.y); pan(next.x - pinch.x, next.y - pinch.y); }
    pinch = next;
  } else if (pointers.size === 1) {
    if (previous.button === 2 || previous.button === 1 || previous.shift || e.shiftKey) pan(dx, dy);
    else { goal.azimuth -= dx * .004; goal.elevation = clamp(goal.elevation + dy * .003, .13, 1.4); }
  }
});
for (const name of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) canvas.addEventListener(name, e => { pointers.delete(e.pointerId); pinch = null; });
canvas.addEventListener('wheel', e => { e.preventDefault(); moved(); zoom(Math.exp(clamp(e.deltaY, -200, 200) * .0015), e.clientX, e.clientY); }, { passive: false });
canvas.addEventListener('dblclick', e => { const point = groundAt(e.clientX, e.clientY); if (point) { goal.target = point; goal.distance = Math.max(25, goal.distance * .65); moved(); } });

async function fullscreen() {
  try { if (document.fullscreenElement) await document.exitFullscreen(); else await document.documentElement.requestFullscreen(); }
  catch { /* Some browsers disallow fullscreen in embedded previews. */ }
}
$('day').onclick = () => setHour(12);
$('night').onclick = () => setHour(22);
$('time-slider').addEventListener('input', e => setHour(Number((e.target as HTMLInputElement).value)));
document.addEventListener('keydown', e => {
  if ((e.target as HTMLElement).matches('input,textarea,[contenteditable=true]')) return;
  if (e.key.toLowerCase() === 'h') setView('village');
  if (e.key.toLowerCase() === 'f') void fullscreen();
  if (e.target !== canvas) return;
  if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', '+', '-', '='].includes(e.key)) {
    e.preventDefault(); moved();
    if (e.key === 'ArrowLeft') goal.azimuth -= .09;
    if (e.key === 'ArrowRight') goal.azimuth += .09;
    if (e.key === 'ArrowUp') goal.elevation = clamp(goal.elevation + .05, .13, 1.4);
    if (e.key === 'ArrowDown') goal.elevation = clamp(goal.elevation - .05, .13, 1.4);
    if (e.key === '+' || e.key === '=') goal.distance = Math.max(17, goal.distance * .9);
    if (e.key === '-') goal.distance = Math.min(760, goal.distance * 1.1);
  }
});

function progress(message: string, amount: number) {
  loading.setAttribute('aria-label', message);
  $('loading-bar').style.transform = `scaleX(${amount})`;
  document.querySelector('[role=progressbar]')?.setAttribute('aria-valuenow', String(Math.round(amount * 100)));
}
function fail(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  errors.push(message); console.error('[shirakawago]', message);
  loading.classList.remove('done'); loading.classList.add('error');
  progress('Unable to load the village. Use the retry button to try again.', 1);
  api.ready = false; window.__sceneReady = false;
  canvas.inert = true;
  for (const overlay of overlays) overlay.inert = true;
  document.body.classList.remove('ready');
  $('retry').hidden = false;
  $('retry').focus({ preventScroll: true });
}
$('retry').onclick = () => location.reload();
window.__SCENE_ERRORS__ = errors;
const api: SceneApi = window.__shirakawago = {
  ready: false, errors, engine,
  stats: () => ({ ...(engine ? JSON.parse(engine.stats()) : {}), ready: api.ready, hour, view: currentView, frame, animationSeconds: liveSeconds, camera: api.camera(), performance: { fps, gpuFrameMs, renderDpr, targetFps: mobile ? 30 : null, pendingFrames: gpuPending, maxPendingFrames, startupMs, buildMs }, errors }),
  setHour, setView, setCamera,
  camera: () => ({ position: safePosition(), target: [...pose.target], fov: pose.fov }),
  frameDone: async () => { await engine?.frameDone(); },
};
// Explicit diagnostic URL for tethered-device QA when Web Inspector is disabled.
// The normal experience remains entirely text-free.
if (params.get('diagnostics') === '1') {
  const diagnostic = document.createElement('pre');
  diagnostic.style.cssText = 'position:fixed;z-index:100;top:0;left:0;right:0;max-height:80vh;overflow:auto;margin:0;padding:24px 12px;background:#101827ed;color:#fff;font:11px/1.35 monospace;white-space:pre-wrap;overflow-wrap:anywhere;pointer-events:none';
  document.body.append(diagnostic);
  setInterval(() => {
    const state = api.stats();
    diagnostic.textContent = JSON.stringify({ ready: api.ready, errors, gpuError: state.error, quality: state.quality, triangles: state.triangles, reflectionUpdates: state.reflectionUpdates, ...state.performance as object }, null, 2);
  }, 500);
}
if (params.get('shot') === '1' || params.get('ui') === '0' || params.get('gui') === '0') document.body.classList.add('shot');
function resize() {
  resizePending = true;
}
function applyResize() {
  const pixelBudgetDpr = Math.sqrt(900_000 / Math.max(1, canvas.clientWidth * canvas.clientHeight));
  renderDpr = clamp(finite('dpr', Math.min(devicePixelRatio, mobile ? Math.min(adaptiveDpr, pixelBudgetDpr) : 1.75)), .5, 2);
  engine?.resize(canvas.clientWidth, canvas.clientHeight, renderDpr);
  resizePending = false;
}
window.addEventListener('resize', resize);
document.addEventListener('visibilitychange', () => { last = 0; timingStart = 0; timingFrames = 0; });
function tick(now: number) {
  if (!engine || errors.length) return;
  if (document.hidden || (mobile && (gpuPending >= maxPendingFrames || (last && now - last < 1000 / 30 - 2)))) {
    requestAnimationFrame(tick); return;
  }
  const elapsed = last ? (now - last) / 1000 : 1 / 60;
  const dt = Math.min(.05, elapsed); last = now;
  if (params.get('diagnostics') === '1' && params.get('motion') === '1') goal.azimuth += dt * .06;
  const k = reducedMotion ? 1 : 1 - Math.exp(-dt * 6);
  if (engine.groundHeight) goal.target[1] = Math.max(goal.target[1], engine.groundHeight(goal.target[0], goal.target[2]) + 2.2);
  pose.azimuth += (goal.azimuth - pose.azimuth) * k;
  pose.elevation += (goal.elevation - pose.elevation) * k;
  pose.distance *= Math.pow(goal.distance / pose.distance, k);
  pose.fov += (goal.fov - pose.fov) * k;
  pose.target = pose.target.map((v, i) => v + (goal.target[i] - v) * k) as V3;
  liveSeconds = params.has('freeze') ? finite('freeze', 8) : liveSeconds + Math.min(elapsed, .5);
  try {
    // Reconfigure and draw in the same animation callback. Resizing from a GPU
    // completion callback can otherwise present a cleared canvas between frames.
    if (resizePending) applyResize();
    engine.setCamera(new Float32Array(safePosition()), new Float32Array(pose.target), pose.fov);
    engine.render(liveSeconds);
    frame++;
    timingFrames++;
    if (!timingStart) timingStart = now;
    if (now - timingStart >= 1500) {
      fps = Math.round(timingFrames * 1000 / (now - timingStart) * 10) / 10;
      timingFrames = 0; timingStart = now;
    }
    if (mobile) {
      // Keep two frames at most. Safari GPU completion callbacks can lag actual
      // presentation; strict one-frame serialization needlessly stalls the GPU.
      gpuPending++;
      const submitted = performance.now();
      void engine.frameDone().then(() => {
        const duration = performance.now() - submitted;
        gpuFrameMs = gpuFrameMs ? gpuFrameMs * .9 + duration * .1 : duration;
        gpuPending = Math.max(0, gpuPending - 1);
        if (!params.has('dpr') && frame > 30 && submitted - lastAdaptation > 4000) {
          const next = gpuFrameMs > 29 * maxPendingFrames ? Math.max(.8, adaptiveDpr - .1)
            : gpuFrameMs < 19 * maxPendingFrames ? Math.min(1.25, adaptiveDpr + .05) : adaptiveDpr;
          lastAdaptation = submitted;
          if (Math.abs(next - adaptiveDpr) > .01) { adaptiveDpr = next; resize(); }
        }
      }).catch(error => { gpuPending = Math.max(0, gpuPending - 1); fail(error); });
    }
    if (frame % 60 === 0) { const error = JSON.parse(engine.stats()).error; if (error) throw new Error(String(error)); }
  } catch (error) { fail(error); return; }
  requestAnimationFrame(tick);
}
async function start() {
  const started = performance.now();
  if (!('gpu' in navigator)) throw new Error('This little world needs WebGPU. Open it in a current version of Chrome, Edge, or Safari with WebGPU enabled.');
  progress('Preparing a little winter…', .15);
  const moduleUrl = new URL(`${import.meta.env.BASE_URL}wasm/${__WASM_REVISION__ ? __WASM_REVISION__ + '/' : ''}shirakawago_web.js`, location.href).href;
  const module = await import(/* @vite-ignore */ moduleUrl);
  await module.default();
  progress('Building roofs, trees, and snowdrifts…', .38);
  await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
  const buildStarted = performance.now();
  engine = await module.initScene(canvas, mobile); api.engine = engine;
  buildMs = performance.now() - buildStarted;
  resize(); setHour(hour);
  progress('Lighting the windows…', .88);
  if (!reducedMotion && !params.has('cam') && !params.has('freeze') && params.get('intro') !== '0') { pose.distance *= 1.09; pose.elevation += .035; pose.azimuth -= .045; }
  requestAnimationFrame(tick);
  for (let i = 0; i < 4; i++) await new Promise(resolve => requestAnimationFrame(resolve));
  await engine!.frameDone();
  const rendererError = JSON.parse(engine!.stats()).error;
  if (rendererError) throw new Error(String(rendererError));
  progress('Welcome to Shirakawa-go.', 1);
  api.ready = true; window.__sceneReady = true;
  startupMs = performance.now() - started;
  for (const overlay of overlays) overlay.inert = false;
  canvas.inert = false;
  loading.classList.add('done'); document.body.classList.add('ready');
}
setHour(hour);
void start().catch(fail);
